import { useState, type ReactNode } from 'react';
import { ArtifactWorkspace, Icon, icons, theme } from '@agentisai/design-system';
import type { TurnArtifact } from '@agentisai/shared-types';
import { formatArtifactSize, formatArtifactType } from '../lib/formatArtifactMeta';
import { useGetArtifact } from '../queries/useGetArtifact';
import { ArtifactPreview } from './ArtifactPreview';

const muted = {
  padding: theme.space[5],
  margin: 0,
  color: theme.color.neutral.muted,
  fontSize: theme.typography.fontSize.bodySm,
  lineHeight: theme.typography.lineHeight.body,
} as const;

/**
 * The opaque `{artifactId}` for a ledger entry — the uuid segment of its S3 key
 * (`workstreams/{ws}/artifacts/{uuid}/{filename}`, the ADR-0039 write layout;
 * ADR-0046 made that segment the wire identity). An off-layout key can't be
 * addressed and returns null — the panel then shows a no-preview note instead
 * of firing a doomed request.
 */
function artifactIdOf(key: string): string | null {
  const parts = key.split('/');
  return parts.length === 5 && parts[0] === 'workstreams' && parts[2] === 'artifacts'
    ? (parts[3] ?? null)
    : null;
}

/**
 * WorkstreamArtifacts — the persistent deliverables panel (Stage 3c). Feeds the
 * DS `ArtifactWorkspace` (tab strip + metadata strip) from the workstream's
 * artifact ledger; the S3 `key` is the durable identity (ADR-0040) and the tab id.
 *
 * Drop-7 S02: the active tab's content/URL come from `getArtifact` — the same
 * re-presign path the Files rail uses (ADR-0045/0046) — replacing the Stage-3c
 * held-cache + client-computed-expiry pattern. A deliverable stays viewable and
 * downloadable indefinitely; no "link expired, ask the agent to re-share".
 */
export function WorkstreamArtifacts({
  workstreamId,
  artifacts,
}: {
  /** The created workstream the ledger belongs to (the panel only renders post-create). */
  readonly workstreamId: string;
  /** The cross-turn artifact ledger (metadata; ADR-0041). */
  readonly artifacts: readonly TurnArtifact[];
}) {
  // Active tab — component-local (frontend-architecture R1.0): the workstream
  // itself isn't URL-addressable yet, so neither is its active deliverable.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const active = artifacts.find((a) => a.key === activeKey) ?? artifacts[0];
  const artifactId = active ? artifactIdOf(active.key) : null;
  const content = useGetArtifact(workstreamId, artifactId ?? '', artifactId !== null);
  if (!active) return null;

  const data = content.data?.data;
  let body: ReactNode;
  let metaRight: ReactNode = null;
  if (artifactId === null) {
    body = <p style={muted}>No preview available for this deliverable.</p>;
  } else if (content.isPending) {
    body = <p style={muted}>Loading preview…</p>;
  } else if (content.isError || !data) {
    body = <p style={muted}>Couldn&rsquo;t open this deliverable. Please try again.</p>;
  } else {
    metaRight = data.url ? (
      <a
        href={data.url}
        download={data.filename}
        style={{ color: theme.color.accent.blue[700], font: 'inherit' }}
      >
        Download
      </a>
    ) : null;
    // Map the fetched artifact onto the shape ArtifactPreview renders (content →
    // preview, the opaque id → the identity key) — same mapping as FileViewPanel.
    body = data.content ? (
      <ArtifactPreview
        artifact={{
          filename: data.filename,
          contentType: data.contentType,
          sizeBytes: data.sizeBytes,
          key: data.artifactId,
          url: data.url,
          preview: data.content,
        }}
      />
    ) : (
      <p style={muted}>
        No inline preview for this file type{data.url ? ' — use Download to open it.' : '.'}
      </p>
    );
  }

  return (
    <ArtifactWorkspace
      tabs={artifacts.map((a) => ({
        id: a.key,
        label: a.filename,
        icon: <Icon as={icons.FileText} size={14} />,
      }))}
      activeId={active.key}
      onSelect={setActiveKey}
      metaLeft={
        <>
          {formatArtifactType(active.contentType)} · {formatArtifactSize(active.sizeBytes)}
        </>
      }
      metaRight={metaRight}
    >
      {body}
    </ArtifactWorkspace>
  );
}
WorkstreamArtifacts.displayName = 'WorkstreamArtifacts';
